import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import LoadingSpinner from "./LoadingSpinner";

const API_BASE = process.env.REACT_APP_API_URL || "";

const inferSeasonNumber = (seasonId) => {
  if (!seasonId) return 1;
  const match = String(seasonId).match(/(\d+)/);
  const n = match ? parseInt(match[1], 10) : NaN;
  return Number.isFinite(n) ? n : 1;
};

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toMsTimestamp(v, fallback = 0) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return fallback;
    const asNum = Number(s);
    if (Number.isFinite(asNum)) return asNum;
    const parsed = Date.parse(s);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  if (v instanceof Date) return v.getTime();
  return fallback;
}

function formatCountdownNoSeconds(ms) {
  const totalSeconds = Math.max(0, Math.floor((ms || 0) / 1000));
  const d = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);

  const pad = (n) => String(n).padStart(2, "0");
  if (d > 0) return `${d}D ${pad(h)}H ${pad(m)}Min`;
  if (h > 0) return `${h}H ${pad(m)}Min`;
  return `${m}Min`;
}

function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, "0");

  if (days > 0) {
    return `${days}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const bgStyle = {
  overflowY: "auto",
  padding: 0,
};

const rowWrapStyle = {
  width: "100%",
  padding: 5,
  boxSizing: "border-box",
  float: "left",
  marginBottom: 5,
};

const leftBoxBaseStyle = {
  float: "left",
  padding: 10,
  boxSizing: "border-box",
  position: "relative",
  borderRadius: 12,
  width: 60,
  height: 60,
  backgroundColor: "#1A0101",
};

const leftBoxCountStyle = {
  position: "absolute",
  bottom: 3,
  left: 0,
  width: "100%",
  textAlign: "center",
  color: "#fff",
  textShadow: "1px 1px #000",
  fontSize: 21,
};

const rightPanelBaseStyle = {
  float: "left",
  marginLeft: 4,
  padding: 10,
  boxSizing: "border-box",
  position: "relative",
  borderBottom: "2px solid #444",
  borderRadius: 12,
  width: "calc(100% - 64px)",
  height: 60,
};

const rightTitleBaseStyle = {
  position: "absolute",
  top: 7,
  left: 10,
  width: "calc(100% - 20px)",
  height: 18,
  lineHeight: "18px",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  textAlign: "left",
};

const rightIconBaseStyle = {
  height: 18,
  position: "absolute",
  top: 7,
  right: 10,
};

const progressOuterStyle = {
  backgroundColor: "#000",
  overflow: "hidden",
  position: "absolute",
  bottom: 7,
  left: 10,
  width: "calc(100% - 20px)",
  height: 18,
  borderRadius: 9,
  color: "#fff",
  lineHeight: "19px",
  textShadow: "1px 1px #000",
  fontSize: 14,
};

const progressInnerBaseStyle = {
  borderRadius: 9,
  position: "absolute",
  top: 0,
  left: 0,
  background: "linear-gradient(90.59deg, #BF1515 1.87%, #FD8700 96.96%)",
  height: 18,
};

const progressTextStyle = { zIndex: 1, position: "relative",  fontWeight: 600 };

const topBarStyle = {
  marginTop: 0,
  borderRadius: 12,
  display: "inline-block",
  width: "100%",
  height: 60,
  padding: 10,
  boxSizing: "border-box",
  backgroundColor: "#00000099",
};

/* =========================
   Reward mapping (igual Arenas)
   ========================= */

const REWARD_UI = {
  sparks: { label: "Sparks", icon: "/sparks.png" },
  runes: { label: "Runes", icon: "/runes.png" },
  cardPieces: { label: "Card Pieces", icon: "/pieces.png" },
  card: { label: "Card", icon: "/pack.png" },
  spins: { label: "Spins", icon: "/fortuneicon.png" },
  boosters: { label: "Booster", icon: "/energy.png" },
  abilities: { label: "Ability", icon: "/fightlog.png" },
  skins: { label: "Skin", icon: "/fragment.png" },
  gamepass: { label: "Gamepass", icon: "/passicon.png" },
};

function normalizeReward(m) {
  const r =
    m?.reward ||
    m?.prize ||
    m?.rewards ||
    m?.prizes ||
    m?.rewardObj ||
    null;

  const normalizeTypeName = (type) => {
    const t = String(type || "").trim().toLowerCase();

    const aliases = {
      spark: "sparks",
      sparks: "sparks",

      rune: "runes",
      runes: "runes",

      cardpiece: "cardPieces",
      cardpieces: "cardPieces",
      card_piece: "cardPieces",
      card_pieces: "cardPieces",

      spin: "spins",
      spins: "spins",

      booster: "boosters",
      boosters: "boosters",

      ability: "abilities",
      abilities: "abilities",

      skin: "skins",
      skins: "skins",
    };

    return aliases[t] || t;
  };

  // 1) explicit format: { type, amount }
  if (r && typeof r === "object" && r.type) {
    return {
      type: normalizeTypeName(r.type),
      amount: toNum(r.amount, 0),
    };
  }

  // 2) fallback object format: { sparks: 10 } / { spins: 1 } etc
  const obj = r && typeof r === "object" ? r : null;

  if (obj) {
    for (const key of Object.keys(REWARD_UI)) {
      const amt = toNum(obj[key], 0);
      if (amt > 0) return { type: key, amount: amt };
    }
  }

  return { type: "runes", amount: 0 };
}

function RewardRow({
  leftIcon,
  count,
  borderColor,
  panelColor,
  title,
  rightIcon,
  progressLabel,
  progressPct,
}) {
  const safePct = Math.max(
    0,
    Math.min(100, Number(progressPct) || 0)
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        width: "100%",
        height: 66,
        display: "grid",
        gridTemplateColumns: "66px 1fr",
        gap: 6,
        marginBottom: 10,
        boxSizing: "border-box",
      }}
    >
      {/* REWARD */}
      <div
        style={{
          height: 66,
          borderRadius: 10,
          background: "#1b0503",
          border: `3px solid ${borderColor}`,
          boxShadow:
            "0 4px 0 rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.08)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <img
          src={leftIcon}
          alt=""
          style={{
            width: 42,
            height: 42,
            objectFit: "contain",
            filter: "drop-shadow(0 3px 3px rgba(0,0,0,.75))",
          }}
        />
      </div>

      {/* MILESTONE CONTENT */}
      <div
        style={{
          height: 66,
          position: "relative",
          borderRadius: 10,
          background:
            panelColor === "#222"
              ? "linear-gradient(180deg, #5b2117 0%, #43140d 100%)"
              : "linear-gradient(180deg, #7a2d17 0%, #531308 100%)",
          boxShadow:
            "0 4px 0 rgba(0,0,0,.45), inset 0 1px 0 rgba(255,210,120,.16)",
          overflow: "hidden",
          boxSizing: "border-box",
          padding: "7px 10px",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "url('/bricks.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.07,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            paddingRight: 43,
            color: "#fff",
            fontSize: 14,
            lineHeight: "17px",
            fontWeight: 600,
            textAlign: "left",
            textShadow: "2px 3px 0 #000",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>

        <img
          src={rightIcon}
          alt=""
          style={{
            position: "absolute",
            right: 8,
            top: 7,
            zIndex: 5,
            width: 21,
            height: 21,
            objectFit: "contain",
            filter: "drop-shadow(0 3px 2px rgba(0,0,0,.75))",
          }}
        />

        <div
  style={{
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 8,
    height: 23,
            borderRadius: 999,
            background: "#130100",
            overflow: "hidden",
            zIndex: 2,
            boxShadow: "inset 0 2px 4px rgba(0,0,0,.9)",
          }}
        >
          <div
            style={{
              width: `${safePct}%`,
              height: "100%",
              borderRadius: 999,
              background:
                "linear-gradient(90deg, #c94b38 0%, #df6a38 52%, #eea44a 100%)",
            }}
          />

          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 12,
              lineHeight: "23px",
              fontWeight: 700,
              textShadow: "2px 2px 0 #000",
              zIndex: 4,
              whiteSpace: "nowrap",
            }}
          >
            {progressLabel}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ConfirmEnterModal({
  open,
  price,
  currency,
  onClose,
  onConfirm,
  loading,
}) {
  const isStars = currency === "stars";

  return (
    <AnimatePresence>
      {open && (
        <div className="modal_box" onClick={onClose}>
        <div className="modal_title">Confirm<button onClick={onClose} className="modal_close"/></div>
          <motion.div
            className="modal_body"   initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
          <div className="modal_inside">
            <img
              src={isStars ? "/finger.png" : "/finger.png"}
              alt={isStars ? "Stars" : "Runes"}
              style={{ height: 80, marginBottom: 10 }}
            />

            <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: 10 }}>
              Confirm Entry
            </div>

            <div className="info" style={{ fontSize: 18}}>
              Enter this event for <b>{price}</b> {isStars ? "stars" : "runes"}?
            </div>

            </div>

            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}} onClick={onConfirm} disabled={loading}>
                {loading ? (isStars ? "Opening..." : "Entering...") : "Confirm"}
              </button>
              <button className="game-button red-button" onClick={onClose} disabled={loading}>
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function NoRunesModal({ open, onClose, onOpenShop }) {
  return (
    <AnimatePresence>
      {open && (
        <div className="modal-backdrop" onClick={onClose}>
          <motion.div
            className="modal fancy-success-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <img
              src="/runes.png"
              alt="Not enough runes"
              style={{ height: 80, marginBottom: 10 }}
            />
            <h2 style={{ color: "#ffcc00", marginBottom: 10 }}>
              Not enough Runes
            </h2>
            <p style={{ textShadow: "1px 1px #000", color: "#ddd" }}>
              You don’t have enough runes.
              <br />
              Convert Sparks into Runes in the Shop and try again.
            </p>

            <div className="modal-footer">
              <button className="buy-runes-btn" onClick={() => onOpenShop?.("coin_2")}>
                Buy Runes
              </button>
              <br />
              <button onClick={onClose}>Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* =========================
   Main
   ========================= */

export default function BattleStreakEvent({
  user,
  token: tokenProp,
  setUser,
  onNavigate,
  onOpenShop, // opcional, igual GamepassPage
}) {

  const { season } = useAppConfig() || {};
  const token = tokenProp || localStorage.getItem("token") || "";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const [now, setNow] = useState(() => Date.now());
  const [showConfirmEnter, setShowConfirmEnter] = useState(false);
  const [showNoRunes, setShowNoRunes] = useState(false);
  const [joining, setJoining] = useState(false);

  const seasonNumber = useMemo(() => {
  if (season) {
    const candidates = [
      season.number,
      season.season_number,
      season.SEASON_NUMBER,
      season.seasonNumber,
    ];

    const n = candidates.find(
      (v) => v !== undefined && v !== null && String(v).trim() !== ""
    );

    if (n !== undefined) return n;
  }

  return inferSeasonNumber(user?.gamepassSeasonId);
}, [season, user?.gamepassSeasonId]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    load();

  }, [token]);

  async function api(path, opts = {}) {
    const res = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers || {}),
      },
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error || `HTTP_${res.status}`);
    return json;
  }

  async function load() {
    try {
      setErr("");
      setLoading(true);
      const res = await api("/api/battle/streak");
      setData(res);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

const safe = data || {};
const milestones = Array.isArray(safe.milestones) ? safe.milestones : [];

const joined = !!safe.joined;
const mustJoin = !!safe.mustJoin;

const totalSettled = toNum(safe.totalSettled, 0);
const priceRunes = toNum(safe.priceRunes, 0);
const priceStars = toNum(safe.priceStars, 0);

const effectiveTotalSettled =
mustJoin && !joined ? 0 : totalSettled;

const shouldUseStars = priceStars > 0;
const effectivePrice = shouldUseStars ? priceStars : priceRunes;

const { eventLabel, eventRemainingMs, eventNotStarted, eventEnded } = useMemo(() => {
  const s = (safe.startDate || safe.start_date || "").trim();
  const e = (safe.endDate || safe.end_date || "").trim();

  const startTs = s ? Date.parse(`${s}T00:00:00.000Z`) : 0;
  const endTs = e ? Date.parse(`${e}T23:59:59.999Z`) : 0;

  if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || !startTs || !endTs) {
    return {
      eventLabel: "EVENT",
      eventRemainingMs: 0,
      eventNotStarted: false,
      eventEnded: false,
    };
  }

  if (now < startTs) {
    return {
      eventLabel: "EVENT STARTS IN",
      eventRemainingMs: startTs - now,
      eventNotStarted: true,
      eventEnded: false,
    };
  }

  if (now <= endTs) {
    return {
      eventLabel: "EVENT ENDS IN",
      eventRemainingMs: endTs - now,
      eventNotStarted: false,
      eventEnded: false,
    };
  }

  return {
    eventLabel: "EVENT ENDED",
    eventRemainingMs: 0,
    eventNotStarted: false,
    eventEnded: true,
  };
}, [safe.startDate, safe.start_date, safe.endDate, safe.end_date, now]);

function clickPrimary() {
  if (eventNotStarted || eventEnded) return;

  if (mustJoin && !joined) {
    if (shouldUseStars) {
      handleConfirmJoin();
    } else {
      setShowConfirmEnter(true);
    }
    return;
  }

  onNavigate?.("arenas");
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pollStarsOrder = async (orderId, maxAttempts = 40, delayMs = 1500) => {
  for (let i = 0; i < maxAttempts; i++) {
    const res = await fetch(
      `${API_BASE}/api/telegram/stars/order/${orderId}`,
      {
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : undefined,
      }
    );

    const data = await res.json().catch(() => ({}));

    if (res.ok && data?.status === "paid") return true;
    if (res.ok && data?.status === "failed") return false;

    await wait(delayMs);
  }

  return false;
};

 async function handleConfirmJoin() {
  try {
    setJoining(true);
    setErr("");

    // ---------- STARS FLOW ----------
    if (shouldUseStars) {
  setShowConfirmEnter(false);

  const res = await fetch(`${API_BASE}/api/battle/streak/purchase-stars`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 403 && data?.code === "NEEDS_HUMAN_CHECK") {
          onNavigate?.("humancheck", { returnTo: "battle-streak" });
          return;
        }

        if (data?.error === "ALREADY_JOINED") {
          setShowConfirmEnter(false);
          await load();
          return;
        }

        setErr(data?.error || "Failed to create stars payment");
        return;
      }

      const invoiceLink =
        data?.invoiceLink ??
        data?.payment?.invoiceLink ??
        null;

      const orderId =
        data?.orderId ??
        data?.payment?.orderId ??
        null;

      if (!invoiceLink || !orderId) {
        setErr("Invalid stars payment response");
        return;
      }

      const tg = window.Telegram?.WebApp;
      if (!tg?.openInvoice) {
        setErr("Telegram Stars payments are only available inside Telegram.");
        return;
      }

      tg.openInvoice(invoiceLink, async (status) => {
        if (status !== "paid") return;

        const paid = await pollStarsOrder(orderId);
        if (!paid) {
          setErr("Payment received but confirmation is still pending.");
          return;
        }

        setShowConfirmEnter(false);
        await load();
      });

      return;
    }

    // ---------- RUNES FLOW ----------
    const currentRunes = Number(user?.runes ?? 0);
    if (currentRunes < priceRunes) {
      setShowConfirmEnter(false);
      setShowNoRunes(true);
      return;
    }

    const res = await fetch(`${API_BASE}/api/battle/streak/join`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      if (res.status === 403 && data?.code === "NEEDS_HUMAN_CHECK") {
        onNavigate?.("humancheck", { returnTo: "battle-streak" });
        return;
      }

      if (data?.error === "NOT_ENOUGH_RUNES") {
        setShowConfirmEnter(false);
        setShowNoRunes(true);
        return;
      }

      if (data?.error === "ALREADY_JOINED") {
        setShowConfirmEnter(false);
        await load();
        return;
      }

      setErr(data?.error || "Failed to join event");
      return;
    }

    setShowConfirmEnter(false);

    if (typeof setUser === "function" && data?.user) {
      setUser((prev) => ({
        ...prev,
        runes: data.user.runes ?? prev?.runes,
      }));
    }

    await load();
  } catch (e) {
    console.error("Battle streak join failed:", e);
    setErr("Network error");
  } finally {
    setJoining(false);
  }
}

  return (
    <div className="gamepass-container" style={bgStyle}>
  <div
    style={{
      padding: 12,
      paddingBottom: 0,
      maxWidth: 720,
      margin: "0 auto",
    }}
  >

{loading ? (
  <div
    style={{
      minHeight: 430,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner text="Loading Rune Hunters" size={64} />
  </div>
) : (
  <>

    <div
      className="gamepass-header"
      style={{
        fontFamily: '"Fredoka", sans-serif',
        marginLeft: -12,
        marginRight: -12,
        height: 150,
        backgroundImage:
          "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 70%), url('/moonsun.png'), url('/gamepassbg.png')",
        backgroundSize: "100% 100%, 70px, 100%",
        backgroundRepeat: "no-repeat, no-repeat, no-repeat",
        backgroundPosition: "center top, left 10px center, center top",
        marginTop: "-12px",
        position: "relative",
      }}
    >
      <div
        className="gp-season-title"
        style={{
          position: "relative",
          height: "100%",
        }}
      >
        <div className="gp-season-label shadow1" style={{fontWeight: 600}}>
          TAPTOPIA Season {seasonNumber}
        </div>

        <div
          className="gp-season-name shadow2"
          style={{
            fontWeight: 600,
            fontSize: 28,
            color: "#fff",
          }}
        >
          Rune Hunters
        </div>

        {/* COUNTDOWN */}
        {!loading && !err && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: -35,
              transform: "translateX(-50%)",
              minWidth: 180,
              height: 60,
              backgroundColor: "rgba(0,0,0,0.6)",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 12px",
              boxSizing: "border-box",
            }}
          >
            <img
              src="/eventclock.png"
              alt=""
              style={{
                width: 32,
                height: 32,
                marginRight: 9,
                flexShrink: 0,
              }}
            />

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                color: "#fff",
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#FFBD48",
                  textTransform: "uppercase",
                }}
              >
                {eventLabel}
              </div>

              <div
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                {eventEnded
                  ? "ENDED"
                  : formatCountdown(eventRemainingMs)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

        <div style={{ clear: "both" }} />

        
        {!loading && err && (
          <div
            style={{
              marginTop: 18,
              background: "#3b0000",
              padding: 14,
              borderRadius: 12,
              textAlign: "center",
              textShadow: "1px 1px #000",
            }}
          >
            <div style={{ fontSize: 22, fontWeight: "bold" }}>FAILED TO LOAD</div>
            <div style={{ marginTop: 6 }}>{err}</div>
            <button style={{ marginTop: 12 }} onClick={load}>
              RETRY
            </button>
          </div>
        )}

     
        {!loading && !err && (
  <button
    className="game-button green-button"
    onClick={clickPrimary}
    disabled={eventNotStarted || eventEnded || joining}
    style={{
      width: "100%",
      marginBottom: "-5px",
      cursor:
        eventNotStarted || eventEnded || joining
          ? "not-allowed"
          : "pointer",
      opacity:
        eventNotStarted || eventEnded || joining
          ? 0.65
          : 1,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "10px",
      boxSizing: "border-box",
    }}
  >
    {joining ? (
      "PLEASE WAIT..."
    ) : eventEnded ? (
      "EVENT ENDED"
    ) : eventNotStarted ? (
      <span>
        Coming Soon{" "}
        <span style={{ fontSize: 18, opacity: 0.9 }}>
          ({formatCountdownNoSeconds(eventRemainingMs)})
        </span>
      </span>
    ) : mustJoin && !joined ? (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
        }}
      >
        Enter For
        <img
          src={shouldUseStars ? "/tstar.svg" : "/runes.png"}
          alt={shouldUseStars ? "Stars" : "Runes"}
          style={{
            height: "1em",
            width: "auto",
          }}
        />
        {effectivePrice}
      </span>
    ) : (
      "Battle Now"
    )}
  </button>
)}

        {/* REWARDS LIST (estilo Arenas) */}
        {!loading && !err && (
          <div style={{ marginTop: 10, marginLeft: -12, marginRight: -12,overflow: "hidden", paddingBottom: 0 }}>

          <div className="modal_title">Milestones</div>
          <div className="modal_body modal_body_2" style={{paddingBottom: 5}}>
            {milestones.map((m, i) => {
  const need = toNum(m?.count, 0);
  
  const reached = effectiveTotalSettled >= need;
  const current = Math.min(effectiveTotalSettled, need || 1);
  const pct = need > 0 ? Math.round((current / need) * 100) : 0;

              const reward = m.reward || {};

const normalizedReward = normalizeReward(m);

const rewardUi = REWARD_UI[normalizedReward.type] || {
  label: "Reward",
  icon: "/pack.png",
};

const leftIcon = rewardUi.icon;
const title = (
  <>
    {need} Wins:{" "}
    <span style={{ color: "#ffdf5f" }}>
      {rewardUi.label}
    </span>
  </>
);
const leftCount = normalizedReward.amount ? String(normalizedReward.amount) : "";

              const rewardType = String(normalizedReward?.type || "").toLowerCase();
              const addedLabel =
                rewardType === "runes" || rewardType === "sparks"
                  ? "Added To Your Balance"
                  : "Added To Your Inventory";

              const progressLabel = reached ? addedLabel : `${effectiveTotalSettled}/${need}`;

              const borderColor = reached ? "rgba(255,223,95,0.95)" : "#222";
              const panelColor = reached ? "rgba(223,86,0,0.22)" : "#222";

              const rightIcon = reached ? "/v2/check.png" : "/v2/lock.png";

              return (
                <RewardRow
                  key={m?.id ?? `${need}_${i}`}
                  leftIcon={leftIcon}
                  count={leftCount}
                  borderColor={borderColor}
                  panelColor={panelColor}
                  title={title}
                  rightIcon={rightIcon}
                  progressLabel={progressLabel}
                  progressPct={reached ? 100 : pct}
                />
              );
            })}
          </div></div>
        )}
        </>
    )}
      </div>




      {!shouldUseStars && (
  <ConfirmEnterModal
    open={showConfirmEnter}
    price={priceRunes}
    currency="runes"
    onClose={() => setShowConfirmEnter(false)}
    onConfirm={handleConfirmJoin}
    loading={joining}
  />
)}

      <NoRunesModal
  open={showNoRunes}
  onClose={() => setShowNoRunes(false)}
  onOpenShop={onOpenShop}
/>
    </div>
  );
}